import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFileAlt, FaCalendarDay, FaCalendarWeek, FaCalendarAlt, FaFolderOpen, FaExclamationCircle, FaUsers, FaList, FaDatabase, FaDownload } from 'react-icons/fa';
import { ReportType, RECORDS_PER_PAGE_OPTIONS } from '../types/report.types';
import { RootState } from '../redux/store';
import { useAppDispatch } from '../redux/store';
import { 
    fetchFilesForWeek,
    fetchFileBasedReport,
    fetchDailyReport,
    fetchWeeklyReport,
    fetchMonthlyReport,
    downloadDailyReport,
    downloadWeeklyReport,
    downloadMonthlyReport,
    downloadFileBasedReport
} from '../redux/actions/reportActions';
import { setPage, setItemsPerPage, clearReports } from '../redux/slices/reportSlice';
import { ReportContent } from './ReportContent';
import { getWeekNumber } from '../utils/dateUtils';
import ReturnToDashboard from './ReturnToDashboard';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, addMonths, subYears, addYears } from 'date-fns';
import styles from './Reports.module.css';
import { format as dateFnsFormat } from 'date-fns';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartData,
    ChartOptions
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ScriptableContext } from 'chart.js';

// Register ChartJS components
ChartJS.register(
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ReportCharts: React.FC<{ summary: any, reportType?: string }> = ({ summary, reportType }) => {
    const isFileBasedReport = reportType === 'file';

    // Map data based on report type
    const getData = () => {
        if (isFileBasedReport) {
            return {
                processed: parseInt(summary.processed_records || '0'),
                failed: parseInt(summary.failed_records || '0'),
                pending: parseInt(summary.pending_records || '0'),
                validation_failed: parseInt(summary.validation_failed || '0'),
                total_records: parseInt(summary.total_records || '0'),
                success_rate: summary.success_rate,
                total_plans: summary.total_plans
            };
        }
        return {
            processed: parseInt(summary.processed_count || '0'),
            failed: parseInt(summary.failed_count || '0'),
            pending: parseInt(summary.pending_count || '0'),
            validation_failed: parseInt(summary.validation_failed_count || '0'),
            total_records: parseInt(summary.total_records || '0'),
            success_rate: summary.success_rate,
            total_plans: summary.total_plans,
            total_files: summary.total_files,
            total_employers: summary.total_employers
        };
    };

    const data = getData();
    const totalFailedCount = data.failed + data.validation_failed;

    // Professional dashboard color palette with gradients
    const colors = {
        // Primary colors
        primary: '#4C6FFF',        // Royal blue
        
        // Status colors - more harmonious palette
        processed: '#34D399',      // Softer green
        failed: '#F87171',         // Softer red
        notProcessed: '#94A3B8',   // Lighter slate gray
        
        // Failed records colors - professional blue variations
        processingFailed: '#60A5FA',  // Light blue
        validationFailed: '#3B82F6',  // Medium blue
        
        // Success rate colors
        successHigh: '#34D399',    // High success - green
        successMed: '#60A5FA',     // Medium success - blue
        successLow: '#F87171',     // Low success - red
        
        // UI colors
        background: '#FFFFFF',
        text: '#334155',
        lightBg: '#F8FAFC',
        chartBg: '#E2E8F0'         // Slightly darker background for better contrast
    };

    // Function to get success rate color based on value
    const getSuccessRateColor = (rate: number): string => {
        if (rate >= 80) return colors.successHigh;
        if (rate >= 50) return colors.successMed;
        return colors.successLow;
    };

    // Status Distribution Data
    const statusData: ChartData<'doughnut'> = {
        labels: ['Processed', 'Failed', 'Not Processed'],
        datasets: [{
            data: [
                data.processed,
                totalFailedCount,
                data.pending
            ],
            backgroundColor: [
                colors.processed,
                colors.failed,
                colors.notProcessed
            ],
            borderColor: colors.background,
            borderWidth: 2,
            hoverOffset: 4,
            spacing: 2
        }]
    };

    // Failed Records Breakdown Data
    const failureData: ChartData<'doughnut'> = {
        labels: ['Processing Failed', 'Validation Failed'],
        datasets: [{
            data: [
                data.failed,
                data.validation_failed
            ],
            backgroundColor: [
                colors.processingFailed,
                colors.validationFailed
            ],
            borderColor: colors.background,
            borderWidth: 2,
            hoverOffset: 4,
            spacing: 2
        }]
    };

    // Success Rate Data
    const successRateData: ChartData<'doughnut'> = {
        labels: ['Success Rate', 'Remaining'],
        datasets: [{
            data: data.success_rate ? [parseFloat(data.success_rate), 100 - parseFloat(data.success_rate)] : [0, 100],
            backgroundColor: [
                getSuccessRateColor(parseFloat(data.success_rate || '0')),
                colors.chartBg
            ],
            borderColor: colors.background,
            borderWidth: 2,
            circumference: 180,
            rotation: 270,
            spacing: 0
        }]
    };

    // Style for the gauge value
    const gaugeValueStyle = {
        color: getSuccessRateColor(parseFloat(data.success_rate || '0')),
        fontSize: '2.5rem',
        fontWeight: 'bold' as const
    };

    const gaugeTextStyle = {
        color: colors.text,
        fontSize: '0.875rem',
        marginTop: '0.5rem'
    };

    // Update pie options for better visual
    const pieOptions: ChartOptions<'doughnut'> = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '65%',
        plugins: {
            legend: {
                position: 'bottom' as const,
                labels: {
                    padding: 20,
                    usePointStyle: true,
                    pointStyle: 'circle',
                    font: {
                        family: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                        size: 12,
                        weight: 'normal' as const
                    },
                    color: colors.text
                }
            },
            tooltip: {
                backgroundColor: colors.lightBg,
                titleColor: colors.text,
                titleFont: {
                    size: 14,
                    weight: 'bold' as const,
                    family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
                },
                bodyColor: colors.text,
                bodyFont: {
                    size: 13,
                    weight: 'normal' as const,
                    family: "'Inter', 'Segoe UI', 'Roboto', sans-serif"
                },
                borderColor: '#E2E8F0',
                borderWidth: 1,
                padding: 12,
                boxPadding: 4,
                callbacks: {
                    label: function(context: any) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} (${percentage}%)`;
                    }
                }
            }
        }
    };

    const gaugeOptions: ChartOptions<'doughnut'> = {
        ...pieOptions,
        cutout: '85%',
        plugins: {
            ...pieOptions.plugins,
            legend: {
                display: false
            },
            tooltip: {
                enabled: false
            }
        }
    };

    return (
        <div className={styles.chartsContainer}>
            <div className={styles.statsRow}>
                {!isFileBasedReport && (
                    <>
                        <div className={styles.statBox}>
                            <div className={styles.statIcon}>
                                <FaFileAlt />
                            </div>
                            <div className={styles.statContent}>
                                <h4>Total Files</h4>
                                <p>{data.total_files}</p>
                            </div>
                        </div>
                        <div className={styles.statBox}>
                            <div className={styles.statIcon}>
                                <FaUsers />
                            </div>
                            <div className={styles.statContent}>
                                <h4>Total Employers</h4>
                                <p>{data.total_employers}</p>
                            </div>
                        </div>
                    </>
                )}
                <div className={styles.statBox}>
                    <div className={styles.statIcon}>
                        <FaList />
                    </div>
                    <div className={styles.statContent}>
                        <h4>Total Plans</h4>
                        <p>{data.total_plans}</p>
                    </div>
                </div>
                <div className={styles.statBox}>
                    <div className={styles.statIcon}>
                        <FaDatabase />
                    </div>
                    <div className={styles.statContent}>
                        <h4>Total Records</h4>
                        <p>{data.total_records}</p>
                    </div>
                </div>
            </div>

            <div className={styles.chartRow}>
                {data.total_records > 0 && (
                    <div className={styles.chartWrapper}>
                        <h3>Status Distribution</h3>
                        <div className={styles.chartInner}>
                            <Doughnut data={statusData} options={pieOptions} />
                        </div>
                    </div>
                )}
                {totalFailedCount > 0 && (
                    <div className={styles.chartWrapper}>
                        <h3>Failed Records Breakdown</h3>
                        <div className={styles.chartInner}>
                            <Doughnut data={failureData} options={pieOptions} />
                        </div>
                    </div>
                )}
                <div className={styles.chartWrapper}>
                    <h3>Success Rate</h3>
                    <div className={styles.gaugeChartContainer}>
                        <div className={styles.chartInner}>
                            <Doughnut data={successRateData} options={gaugeOptions} />
                            <div className={styles.gaugeLabel}>
                                <span className={styles.gaugeValue} style={gaugeValueStyle}>
                                    {data.success_rate ? `${parseFloat(data.success_rate).toFixed(1)}%` : 'N/A'}
                                </span>
                                <span className={styles.gaugeText} style={gaugeTextStyle}>
                                    Success Rate
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const Reports: React.FC = () => {
    const dispatch = useAppDispatch();
    const { currentReport, loading, error, pagination, availableFiles } = useSelector((state: RootState) => state.reports);
    const [activeTab, setActiveTab] = useState<ReportType>('daily');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [selectedFile, setSelectedFile] = useState<string>('');
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    // Add refs for managing API requests
    const timeoutIdRef = useRef<NodeJS.Timeout>();
    const controllerRef = useRef<AbortController>();

    // Calculate min and max dates for date pickers
    const getDateLimits = () => {
        const now = new Date();
        
        // Get current time in UTC
        const utcNow = new Date(now.toUTCString());
        
        // For file-based view, allow selection of next UTC day
        const shouldIncludeNextDay = activeTab === 'file';
        
        // Set the max date, including next day for file-based view
        const maxDate = new Date(Date.UTC(
            utcNow.getUTCFullYear(),
            utcNow.getUTCMonth(),
            utcNow.getUTCDate() + (shouldIncludeNextDay ? 1 : 0),
            23, 59, 59, 999
        ));

        const minDate = subYears(maxDate, 1);
        
        return {
            minDate,
            maxDate
        };
    };

    // Get the date range for the selected week
    const getWeekRange = (date: Date) => {
        // Convert to UTC
        const utcDate = new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        ));
        const start = startOfWeek(utcDate, { weekStartsOn: 0 });
        const end = endOfWeek(utcDate, { weekStartsOn: 0 });
        return {
            start,
            end,
            display: `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
        };
    };

    // Get the date range for the selected month
    const getMonthRange = (date: Date) => {
        // Convert to UTC
        const utcDate = new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        ));
        const start = startOfMonth(utcDate);
        const end = endOfMonth(utcDate);
        return {
            start,
            end,
            display: format(utcDate, 'MMMM yyyy')
        };
    };

    // Get the display text for the current selection
    const getDateRangeDisplay = () => {
        if (!selectedDate) return '';

        switch (activeTab) {
            case 'daily':
                return format(selectedDate, 'MMMM d, yyyy');
            case 'weekly':
                return getWeekRange(selectedDate).display;
            case 'monthly':
                return getMonthRange(selectedDate).display;
            default:
                return '';
        }
    };

    const handleTabChange = (tab: ReportType) => {
        setActiveTab(tab);
        setSelectedFile('');
        if (tab === 'file') {
            dispatch(clearReports());
        }
        dispatch(setPage(1));
    };

    const handleStartDateChange = (date: Date | null) => {
        if (date) {
            const { minDate, maxDate } = getDateLimits();
            
            // Ensure date is within allowed range
            if (date < minDate || date > maxDate) {
                return;
            }

            // Set time to start of day to avoid timezone issues
            const normalizedDate = new Date(date.setHours(0, 0, 0, 0));
            setSelectedDate(normalizedDate);

            if (activeTab === 'file') {
                if (!endDate) {
                    // Calculate end date (30 days from start for 31-day total inclusive)
                    const newEndDate = new Date(normalizedDate);
                    newEndDate.setDate(normalizedDate.getDate() + 30); // Changed from 5 to 30
                    newEndDate.setHours(23, 59, 59, 999); // Set to end of day
                    
                    if (newEndDate > maxDate) {
                        newEndDate.setTime(maxDate.getTime());
                    }
                    setEndDate(newEndDate);
                } else {
                    // Adjust existing end date if needed
                    const maxEndDate = new Date(normalizedDate);
                    maxEndDate.setDate(normalizedDate.getDate() + 30); // Changed from 5 to 30
                    maxEndDate.setHours(23, 59, 59, 999); // Set to end of day
                    
                    if (maxEndDate > maxDate) {
                        maxEndDate.setTime(maxDate.getTime());
                    }
                    
                    // If current end date is invalid, adjust it
                    if (!isValidDateRange(normalizedDate, endDate) || endDate < normalizedDate) {
                        setEndDate(maxEndDate);
                    }
                }
            }
            dispatch(setPage(1));
        }
    };

    const handleEndDateChange = (date: Date | null) => {
        if (date && selectedDate) {
            const { maxDate } = getDateLimits();
            
            // Set time to end of day for end date
            const normalizedDate = new Date(date.setHours(23, 59, 59, 999));
            
            // Ensure end date is not before start date or after max date
            if (normalizedDate < selectedDate || normalizedDate > maxDate) return;

            // Calculate maximum allowed end date
            const maxEndDate = new Date(selectedDate);
            maxEndDate.setDate(selectedDate.getDate() + 30); // Changed from 5 to 30
            maxEndDate.setHours(23, 59, 59, 999);
            
            if (maxEndDate > maxDate) {
                maxEndDate.setTime(maxDate.getTime());
            }
            
            if (normalizedDate <= maxEndDate) {
                setEndDate(normalizedDate);
                dispatch(setPage(1));
            } else {
                setEndDate(maxEndDate);
            }
        }
    };

    // Validate date range is within 31 days (inclusive)
    const isValidDateRange = (start: Date | null, end: Date | null): boolean => {
        if (!start || !end) return false;
        
        // Create new Date objects to avoid modifying the originals
        const startDate = new Date(start);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(end);
        endDate.setHours(23, 59, 59, 999);
        
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 30; // 31 days inclusive
    };

    // Calculate minimum and maximum dates for end date picker
    const getEndDateLimits = (): { min: Date | undefined, max: Date | undefined } => {
        if (!selectedDate) return { min: undefined, max: undefined };
        
        const { maxDate } = getDateLimits();
        
        // Set minimum to selected start date
        const minDate = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate()
        ));
        
        // Calculate max end date (31 days total range)
        const maxEndDate = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate() + 30,
            23, 59, 59, 999
        ));
        
        // Ensure we don't exceed the overall maxDate
        if (maxEndDate > maxDate) {
            maxEndDate.setTime(maxDate.getTime());
        }
        
        return { min: minDate, max: maxEndDate };
    };

    // Format date for API calls
    const formatDateForApi = (date: Date, isEndDate: boolean = false): string => {
        // Create a UTC date that includes the full day for end dates
        const utcDate = new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            isEndDate ? 23 : 0,
            isEndDate ? 59 : 0,
            isEndDate ? 59 : 0,
            isEndDate ? 999 : 0
        ));
        return utcDate.toISOString().split('T')[0];
    };

    const handleFileSelect = (fileId: string) => {
        setSelectedFile(fileId);
        dispatch(setPage(1)); // Reset to first page when changing file
    };

    const handlePageChange = (newPage: number) => {
        // Prevent page change if already loading
        if (loading) return;
        
        dispatch(setPage(newPage));
    };

    const handleRecordsPerPageChange = (newPerPage: number) => {
        // Prevent change if already loading
        if (loading) return;
        
        dispatch(setItemsPerPage(newPerPage));
        dispatch(setPage(1)); // Reset to first page when changing records per page
    };

    // Fetch files when date range changes for file-based reports
    useEffect(() => {
        if (activeTab === 'file' && selectedDate && endDate) {
            // Add a small delay to prevent multiple rapid calls
            const timeoutId = setTimeout(() => {
                dispatch(fetchFilesForWeek({
                    start_date: formatDateForApi(selectedDate),
                    end_date: formatDateForApi(endDate, true)  // Pass true for end date
                }));
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [activeTab, selectedDate, endDate, dispatch]);  // Only depend on date changes and tab

    // Display files section
    const renderFilesList = () => {
        if (!availableFiles || availableFiles.length === 0) {
            return (
                <div className={styles.noFilesMessage}>
                    <FaFolderOpen className={styles.noFilesIcon} />
                    <h3>No Files Available</h3>
                    <p>There are no files available for the selected date range.</p>
                    <p>Please select a different date range within a 7-day period.</p>
                </div>
            );
        }

        return (
            <div className={styles.fileSelectContainer}>
                <label className={styles.formLabel}>Select File:</label>
                <select
                    value={selectedFile}
                    onChange={(e) => handleFileSelect(e.target.value)}
                    className={styles.select}
                >
                    <option value="">Choose a file...</option>
                    {availableFiles.map(file => (
                        <option key={file.file_id} value={file.file_id}>
                            {`${file.file_name}${file.process_date ? ` (${new Date(file.process_date).toLocaleDateString()})` : ''}`}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    // Set initial end date when start date changes
    useEffect(() => {
        // Only update endDate if we don't have one set or if activeTab just changed to 'file'
        if (activeTab === 'file' && selectedDate && (!endDate || !isValidDateRange(selectedDate, endDate))) {
            // Create UTC date without timezone offset
            const utcEnd = new Date(Date.UTC(
                selectedDate.getFullYear(),
                selectedDate.getMonth(),
                selectedDate.getDate() + 30, // 30 days after start date = 31 days total
                23, 59, 59, 999
            ));
            
            // Ensure we don't exceed maxDate
            const { maxDate } = getDateLimits();
            if (utcEnd > maxDate) {
                utcEnd.setTime(maxDate.getTime());
            }
            
            setEndDate(utcEnd);
        }
    }, [selectedDate, activeTab]);

    // Determine if we should fetch the report
    const shouldFetchReport = useMemo(() => {
        if (!selectedDate) return false;
        if (activeTab === 'file') {
            return Boolean(selectedFile && endDate);
        }
        return true;
    }, [activeTab, selectedDate, selectedFile, endDate]);

    useEffect(() => {
        if (!shouldFetchReport) return;

        // Cancel previous request if it exists
        if (controllerRef.current) {
            controllerRef.current.abort();
        }

        // Create new AbortController for this request
        controllerRef.current = new AbortController();
        const signal = controllerRef.current.signal;

        // Ensure selectedDate is not null before proceeding
        if (!selectedDate) return;

        const utcDate = new Date(selectedDate);
        utcDate.setMinutes(utcDate.getMinutes() + utcDate.getTimezoneOffset());

        const params = {
            year: utcDate.getUTCFullYear().toString(),
            month: (utcDate.getUTCMonth() + 1).toString(),
            day: utcDate.getUTCDate().toString(),
            page: pagination.page,
            per_page: pagination.per_page,
            sort_field: pagination.sort_field,
            sort_direction: pagination.sort_direction,
            signal
        };

        timeoutIdRef.current = setTimeout(async () => {
            try {
                switch (activeTab) {
                    case 'daily':
                        await dispatch(fetchDailyReport(params));
                        break;
                    case 'weekly':
                        await dispatch(fetchWeeklyReport({
                            ...params,
                            week: getWeekNumber(selectedDate).toString()
                        }));
                        break;
                    case 'monthly':
                        await dispatch(fetchMonthlyReport(params));
                        break;
                    case 'file':
                        if (selectedFile && availableFiles) {
                            const selectedFileInfo = availableFiles.find(f => f.file_id.toString() === selectedFile);
                            if (selectedFileInfo) {
                                await dispatch(fetchFileBasedReport({
                                    fileId: selectedFile,
                                    year: params.year,
                                    month: params.month,
                                    day: params.day,
                                    page: pagination.page,
                                    per_page: pagination.per_page,
                                    sort_field: pagination.sort_field,
                                    sort_direction: pagination.sort_direction,
                                    signal
                                }));
                            }
                        }
                        break;
                }
            } catch (error) {
                console.error('Error fetching report:', error);
            }
        }, 300);

        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
            if (controllerRef.current) {
                controllerRef.current.abort();
            }
        };
    }, [
        shouldFetchReport,
        activeTab,
        selectedDate,
        selectedFile,
        pagination.page,
        pagination.per_page,
        pagination.sort_field,
        pagination.sort_direction,
        dispatch
    ]);

    const handleDownload = async () => {
        if (!selectedDate) return;

        const year = selectedDate.getFullYear().toString();
        const month = (selectedDate.getMonth() + 1).toString().padStart(2, '0');
        const day = selectedDate.getDate().toString().padStart(2, '0');

        try {
            switch (activeTab) {
                case 'daily':
                    await dispatch(downloadDailyReport({ year, month, day }));
                    break;
                case 'weekly':
                    const weekNumber = getWeekNumber(selectedDate).toString().padStart(2, '0');
                    await dispatch(downloadWeeklyReport({ year, week: weekNumber }));
                    break;
                case 'monthly':
                    await dispatch(downloadMonthlyReport({ year, month }));
                    break;
                case 'file':
                    if (selectedFile && availableFiles) {
                        const selectedFileInfo = availableFiles.find(f => f.file_id.toString() === selectedFile);
                        if (selectedFileInfo) {
                            await dispatch(downloadFileBasedReport({
                                fileId: selectedFile,
                                year,
                                month,
                                day,
                                fileName: selectedFileInfo.file_name
                            }));
                        }
                    }
                    break;
            }
        } catch (error) {
            console.error('Error downloading report:', error);
        }
    };

    return (
        <div className={styles.reportsContainer}>
            <div className={styles.pageHeader}>
                <ReturnToDashboard />
            </div>
            <div className={styles.header}>
                <h1>Reports</h1>
            </div>

            <div className={styles.tabContainer}>
                <button
                    className={styles.tabButton}
                    data-active={activeTab === 'daily'}
                    onClick={() => handleTabChange('daily')}
                >
                    <FaCalendarDay /> Daily
                </button>
                <button
                    className={styles.tabButton}
                    data-active={activeTab === 'weekly'}
                    onClick={() => handleTabChange('weekly')}
                >
                    <FaCalendarWeek /> Weekly
                </button>
                <button
                    className={styles.tabButton}
                    data-active={activeTab === 'monthly'}
                    onClick={() => handleTabChange('monthly')}
                >
                    <FaCalendarAlt /> Monthly
                </button>
                <button
                    className={styles.tabButton}
                    data-active={activeTab === 'file'}
                    onClick={() => handleTabChange('file')}
                >
                    <FaFileAlt /> File Based
                </button>
            </div>

            <div className={styles.reportControls}>
                {activeTab === 'file' ? (
                    <div className={styles.dateRangeContainer}>
                        <div className={styles.dateInputGroup}>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>Start Date:</label>
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={handleStartDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={styles.datePicker}
                                    minDate={getDateLimits().minDate}
                                    maxDate={getDateLimits().maxDate}
                                    placeholderText="Select start date"
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>End Date:</label>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleEndDateChange}
                                    dateFormat="yyyy-MM-dd"
                                    className={styles.datePicker}
                                    minDate={selectedDate || undefined}
                                    maxDate={getEndDateLimits().max}
                                    placeholderText="Select end date"
                                    disabled={!selectedDate}
                                />
                            </div>
                        </div>
                        {selectedDate && endDate && (
                            <div className={styles.dateRangeInfo}>
                                <span>Selected period: {Math.floor((endDate.getTime() - selectedDate.getTime()) / (1000 * 60 * 60 * 24) + 1)} days</span>
                            </div>
                        )}
                        
                        {renderFilesList()}
                    </div>
                ) : (
                    <div className={styles.dateSelection}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                {activeTab === 'monthly' ? 'Select Month:' : 
                                 activeTab === 'weekly' ? 'Select Week:' : 
                                 'Select Date:'}
                            </label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleStartDateChange}
                                dateFormat={
                                    activeTab === 'monthly' ? 'MMM yyyy' :
                                    activeTab === 'weekly' ? "'Week of' MMM d, yyyy" :
                                    'yyyy-MM-dd'
                                }
                                className={styles.datePicker}
                                minDate={getDateLimits().minDate}
                                maxDate={getDateLimits().maxDate}
                                placeholderText={
                                    activeTab === 'monthly' ? 'Select month' :
                                    activeTab === 'weekly' ? 'Select week' :
                                    'Select date'
                                }
                                showMonthYearPicker={activeTab === 'monthly'}
                                showFullMonthYearPicker={activeTab === 'monthly'}
                                showFourColumnMonthYearPicker={false}
                                showWeekPicker={activeTab === 'weekly'}
                                useWeekdaysShort
                                formatWeekDay={name => name.slice(0, 3)}
                                calendarStartDay={0}
                            />
                        </div>
                        {selectedDate && (
                            <div className={styles.dateRangeInfo}>
                                <span>{getDateRangeDisplay()}</span>
                                {activeTab === 'weekly' && (
                                    <div className={styles.weekInfo}>
                                        Click on any date to select that week
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Add download button */}
                {selectedDate && (activeTab !== 'file' || selectedFile) && (
                    <div className={styles.downloadSection}>
                        <button
                            className={styles.downloadButton}
                            onClick={handleDownload}
                            disabled={loading}
                        >
                            <FaDownload /> Download Excel Report
                        </button>
                    </div>
                )}
            </div>

            {loading && (
                <div className={styles.loadingMessage}>
                    <div className={styles.spinner}></div>
                    <p>Loading report data...</p>
                </div>
            )}
            
            {error && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorMessage}>
                        <div className={styles.errorIconWrapper}>
                            <FaExclamationCircle className={styles.errorIcon} />
                        </div>
                        <h3>No Report Available</h3>
                        <p>
                            {error.includes('File not found') || error.includes('API Error')
                                ? 'The requested report data is not available. Please select a different file or date range.'
                                : 'Unable to load the report. Please try again later.'}
                        </p>
                    </div>
                </div>
            )}
            {!error && currentReport && (activeTab !== 'file' || (activeTab === 'file' && selectedFile)) && (
                <>
                    <ReportCharts summary={currentReport.summary} reportType={activeTab} />
                    <ReportContent
                        report={currentReport}
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleRecordsPerPageChange}
                    />
                </>
            )}
        </div>
    );
};

export default Reports;